function browserAudioContextClass() {
  return globalThis.window?.AudioContext || globalThis.window?.webkitAudioContext || null;
}

export function resolveSoundProfile(card = {}) {
  const text = `${card.effect || ""} ${card.tag || card.cardTag || ""} ${card.name || card.cardName || ""} ${card.image || card.cardImage || ""}`.toLowerCase();

  if (text.includes("heal")) return { kind: "heal", volume: 0.08 };
  if (text.includes("reflect") || text.includes("spegel")) return { kind: "reflect", volume: 0.11 };
  if (text.includes("shield") || text.includes("minion") || text.includes("gurk") || text.includes("barhandduk")) {
    return { kind: "minion", volume: 0.1 };
  }
  if (text.includes("final") || text.includes("burst") || text.includes("pierce") || text.includes("shot") || text.includes("drop")) {
    return { kind: "scream", volume: 0.13 };
  }
  if (text.includes("damage")) return { kind: "damage", volume: 0.1 };
  return { kind: "default", volume: 0.07 };
}

export function createSoundSchedule(profile) {
  const schedules = {
    heal: [
      { type: "sine", start: 420, end: 820, delay: 0, duration: 0.18, gain: profile.volume },
      { type: "triangle", start: 620, end: 980, delay: 0.05, duration: 0.2, gain: profile.volume * 0.8 },
    ],
    minion: [
      { type: "square", start: 120, end: 85, delay: 0, duration: 0.16, gain: profile.volume },
      { type: "triangle", start: 180, end: 95, delay: 0.07, duration: 0.22, gain: profile.volume * 0.7 },
    ],
    reflect: [
      { type: "sawtooth", start: 260, end: 760, delay: 0, duration: 0.12, gain: profile.volume },
      { type: "sine", start: 760, end: 260, delay: 0.1, duration: 0.16, gain: profile.volume * 0.85 },
    ],
    scream: [
      { type: "sawtooth", start: 980, end: 180, delay: 0, duration: 0.34, gain: profile.volume },
      { type: "square", start: 1280, end: 220, delay: 0.04, duration: 0.28, gain: profile.volume * 0.75 },
      { type: "triangle", start: 1720, end: 520, delay: 0.09, duration: 0.2, gain: profile.volume * 0.55 },
    ],
    damage: [
      { type: "sawtooth", start: 180, end: 60, delay: 0, duration: 0.14, gain: profile.volume },
      { type: "square", start: 95, end: 40, delay: 0.03, duration: 0.12, gain: profile.volume * 0.7 },
    ],
    default: [
      { type: "triangle", start: 300, end: 180, delay: 0, duration: 0.16, gain: profile.volume },
    ],
    // The chance cards' suspense: a click per hop, then the verdict.
    tick: [
      { type: "square", start: 1400, end: 1100, delay: 0, duration: 0.03, gain: profile.volume },
    ],
    win: [
      { type: "triangle", start: 523, end: 523, delay: 0, duration: 0.12, gain: profile.volume },
      { type: "triangle", start: 659, end: 659, delay: 0.11, duration: 0.12, gain: profile.volume },
      { type: "triangle", start: 784, end: 784, delay: 0.22, duration: 0.12, gain: profile.volume },
      { type: "square", start: 1046, end: 1046, delay: 0.33, duration: 0.32, gain: profile.volume * 0.7 },
    ],
    lose: [
      { type: "sawtooth", start: 392, end: 370, delay: 0, duration: 0.24, gain: profile.volume },
      { type: "sawtooth", start: 370, end: 349, delay: 0.26, duration: 0.24, gain: profile.volume },
      { type: "sawtooth", start: 349, end: 262, delay: 0.52, duration: 0.6, gain: profile.volume },
    ],
  };

  return schedules[profile.kind];
}

function playStep(context, step) {
  const start = context.currentTime + step.delay;
  const stop = start + step.duration;
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = step.type;
  oscillator.frequency.setValueAtTime(step.start, start);
  oscillator.frequency.linearRampToValueAtTime(step.end, stop);
  gain.gain.setValueAtTime(step.gain, start);
  gain.gain.linearRampToValueAtTime(0.0001, stop);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(start);
  oscillator.stop(stop);
}

export function createCardSoundBoard({ AudioContextClass = browserAudioContextClass() } = {}) {
  let context = null;

  function getContext() {
    if (!AudioContextClass) return null;
    if (!context) context = new AudioContextClass();
    return context;
  }

  async function prime() {
    const audioContext = getContext();
    if (!audioContext) return false;
    if (audioContext.state === "suspended") await audioContext.resume();
    return true;
  }

  function playCard(card) {
    const audioContext = getContext();
    if (!audioContext) return false;
    if (audioContext.state === "suspended") audioContext.resume();
    createSoundSchedule(resolveSoundProfile(card)).forEach((step) => playStep(audioContext, step));
    return true;
  }

  const CUE_VOLUMES = { tick: 0.05, win: 0.1, lose: 0.1 };

  // The board's gamble cues by name; an unknown one plays nothing.
  function playCue(kind) {
    const audioContext = getContext();
    if (!audioContext || !CUE_VOLUMES[kind]) return false;
    if (audioContext.state === "suspended") audioContext.resume();
    createSoundSchedule({ kind, volume: CUE_VOLUMES[kind] }).forEach((step) => playStep(audioContext, step));
    return true;
  }

  function playCardEvent(event) {
    return playCard({
      effect: event.cardEffect,
      tag: event.cardTag,
      image: event.cardImage,
      name: event.cardName,
    });
  }

  return {
    get context() {
      return context;
    },
    playCard,
    playCardEvent,
    playCue,
    prime,
  };
}
